/** @jsx jsx */

import { jsx, css } from '@emotion/core';

import React, { useState, useEffect, Fragment } from 'react';
import { Grid, Typography, Box, FormHelperText, Button, Divider } from '@material-ui/core';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { DoneRounded } from '@material-ui/icons';
import TextField from '../../text-field';
import { ProceedOrCancelButton } from '../../pge-plus-common/StepperForm';
import routes from '../../../routes';
import { navigate } from 'gatsby';
import useContentMgmt, { LinkedDropdownData } from '../../../hooks/useContentMgmt';
import PopoverToolTip from '../../pge-plus-common/PopoverToolTip';
import WelcomeToPGEPlusModal from '../../pge-plus-common/WelcomeToPGEPlusModal';
import FileUploaderWithOCR from '../../pge-plus-common/FileUploaderWithOCR';
import Modal from '../../pge-plus-common/Modal';
import colors from '../../../themes/main-colors';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import linkedDropdownGroupIds from '../../pge-plus-common/linkedDropdownGroupIds';
import { useEVChargerFormState } from '../../../providers/EVChargerFormStateProvider';
import useFormState from '../../../hooks/useFormState';
import { uploadModelNumberValidateFunction } from '../../pge-plus/ev-charger/rebate-only-flow/rebateOnlyForm.rules';
import { SelectCharger, UpdateEVSectionErrorCode } from '../evcharger.types';
import { pickChargerAndVehicleSelectionFields, pickChargerValidationResultFields, selectChargerFormStateAdapter } from '../../pge-plus-common/utils/pgeplusFormstateAdapters';
import useUpdateEVSections from '../../../hooks/pge-plus/useUpdateEVSections';
import {
  ApplicationEvSectionType,
  IneligibilityReasonCode,
  SectionEvChargerSelection,
  SectionEvChargerSelectionData,
  OcrFieldNames,
} from '../../../__generated__/pgeplus-types';
import useUtil from '../../../hooks/pge-plus/useUtil';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import { SaveAndComeBackLink } from '../../pge-plus-common/SaveAndComeBackLink';
import { DropdownMenu } from '../../pge-plus-common/DropdownMenu';
import { createSelectChargerValidateFunction } from '../../pge-plus-common/PGEPlusForm.rules';

interface EnrollWithVehicleAndChargerProps {
  children?: React.ReactNode;
  path: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    imageTitle: {
      fontSize: '16px !important',
      padding: '0px !important',
      color: `${colors.sparkBlue} !important`,
      fontWeight: 600,
      '&:hover': {
        backgroundColor: 'unset !important',
      },
    },
    errorMessage: {
      fontFamily: 'Untitled-Sans',
      fontSize: '1rem !important',
      letterSpacing: '1px',
    },
    informationBox: {
      borderRadius: 5,
      margin: '20px 0 20px 0',
      padding: 20,
      background: 'white',
    },
    informationItem: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '8px',
    },
    informationCheck: {
      marginRight: '10px',
      color: '#43936F',
    },
  }),
);

export const EnrollWithVehicleAndCharger: React.FC<EnrollWithVehicleAndChargerProps> = props => {
  const theme = useTheme();
  const classes = useStyles(props);
  const [openExamplePhotoModal, setOpenExamplePhotoModal] = useState<boolean>(false,);
  const [ocrExtractionStatus, setOcrExtractionStatus] = useState< string | undefined>();

  const subText1 = {
    fontSize: 20,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  };
  const boldText = {
    fontWeight: 700,
  };

  const { content } = useContentMgmt(microcopyGroupIds.PGE_PLUS_CONFIRM_YOUR_CHARGER);
  const { content: contentMCG, richText, getLinkedDropdown } = useContentMgmt(microcopyGroupIds.PGE_PLUS_EV_CHARGER_FLOW_MCG);
  const { content: fileUploaderText } = useContentMgmt(microcopyGroupIds.PGE_PLUS_UPLOAD_PHOTOS);
  const { content: contentRebateFallback } = useContentMgmt(microcopyGroupIds.PGE_PLUS_REBATE_ONLY_CONFIRM_YOUR_CHARGER);

  const getText = (key: string) =>
    content.get(key) || contentRebateFallback.get(key) || '';

  const [linkedDropdown, setLinkedDropdown] = useState<LinkedDropdownData>({
    parents: [],
    childrenByParent: new Map(),
  });

  useEffect(() => {
    const fetchLinkedDropdown = async () => {
      const data = await getLinkedDropdown({
        linkedDropdownGroupEntryId: 
          linkedDropdownGroupIds.PGE_PLUS_LINKED_DROPDOWN_GROUP_VEHICLE_OPTIONS,
      });
      setLinkedDropdown(data);
    };
    void fetchLinkedDropdown();
  }, []);

  const { parents: vehicleManufacturers, childrenByParent: vehicleModels } = linkedDropdown;

  const {
    formState: { selectCharger, application },
    updateFormState,
    getSectionData,
  } = useEVChargerFormState();

  const sectionDataSelectCharger: SectionEvChargerSelection = getSectionData(
    ApplicationEvSectionType.EvChargerSelection,
  );

  const form = useFormState(selectCharger, {
    validate: uploadModelNumberValidateFunction(),
    validationContext: {
      content,
      params: { vehicleModels },
    },
  });

  const vehicleModelOptions = vehicleModels.get(form.values.vehicleManufacturer || '') || [];

  const { updateSectionChargerSelection } = useUpdateEVSections();
  const { setErrorNotification } = useUtil();
  const { wrapWithLoader } = useWrapWithLoader();

  const navigateToIneligibleFiddlesticks = () => {
    void navigate(routes.PGE_PLUS_EV_CHARGERS_INELIGIBLE, {
      state: {
        errorType: IneligibilityReasonCode.IneligibleProductType,
        showBackButton: true,
        backButtonText: 'REUSABLE_BUTTON_BACK',
        showNextButton: true,
        nextButtonText: 'RETURN_TO_MY_ACCOUNT',
        nextButtonRoute: routes.ACCOUNT,
      },
    });
  };
  
  // NOTE: Reads qualification from the response, not a guess.
  const updateChargerFormState = (
    formValues: Pick<SelectCharger, 'modelNumber' | 'modelNumberImage' | 'vehicleManufacturer' | 'vehicleModel'>,
    sectionData: SectionEvChargerSelectionData | undefined,
  ) => {
    updateFormState('selectCharger', {
      ...pickChargerAndVehicleSelectionFields(formValues),
      ...pickChargerValidationResultFields(sectionData),
    });
  };

  // NOTE: Only keep the old preference if nothing that could change qualification actually changed.
  const chargerOrVehicleInputsChanged = (
    formValues: Pick<SelectCharger, 'modelNumber' | 'modelNumberImage' | 'vehicleManufacturer' | 'vehicleModel'>,
  ) =>
    formValues.modelNumber !== selectCharger?.modelNumber ||
    formValues.modelNumberImage?.id !== selectCharger?.modelNumberImage?.id ||
    formValues.vehicleManufacturer !== selectCharger?.vehicleManufacturer ||
    formValues.vehicleModel !== selectCharger?.vehicleModel;

  const handleSaveAndExit = wrapWithLoader(async () => {
    try {
      const response = await updateSectionChargerSelection(
            selectChargerFormStateAdapter().fromFormState(
              {
                ...pickChargerAndVehicleSelectionFields(form.values),
                enrollmentPreference: chargerOrVehicleInputsChanged(form.values)
                  ? undefined
                  : selectCharger?.enrollmentPreference,
              },
              application?.id!,
              sectionDataSelectCharger?.id,
              true,
            ),
      );
      // NOTE: This always threw before, errors is never empty. Was not noticed since Save and exit btn was not visible previously
      if (response.data?.updateSectionEVChargerSelection?.validation?.success !== true) {
        throw new Error();
      } else {
        updateChargerFormState(form.values, response.data.updateSectionEVChargerSelection.section?.data);
        void navigate(routes.ACCOUNT);
      }
    } catch {
      setErrorNotification(true);
    }
  });

  const onSubmit = wrapWithLoader(async (data: SelectCharger) => {
    try {
      const response = await updateSectionChargerSelection(
            selectChargerFormStateAdapter().fromFormState(
              {
                ...pickChargerAndVehicleSelectionFields(data),
                enrollmentPreference: chargerOrVehicleInputsChanged(data)
                  ? undefined
                  : selectCharger?.enrollmentPreference,
              },
              application?.id!,
              sectionDataSelectCharger?.id,
            ),
      );

      if (response.data?.updateSectionEVChargerSelection.validation?.success) {
        const sectionData = response.data.updateSectionEVChargerSelection.section?.data;
        const chargerIsQualified = sectionData?.chargerValidation?.isQualifiedProduct ?? false;
        const vehicleIsQualified = sectionData?.vehicleValidation?.isQualifiedVehicle ?? false;

        //Store the data in global store
        updateChargerFormState(data, sectionData);

        if (!chargerIsQualified && !vehicleIsQualified) {
          navigateToIneligibleFiddlesticks();
          return;
        }

        // If only charger is qualified, go to contact info
        if (chargerIsQualified && !vehicleIsQualified) {
          void navigate(routes.PGE_PLUS_EV_CHARGERS_CONTACT_INFO);
          return;
        }

        // If both charger and vehicle are qualified, go to choice page (Use my charger or Use my Vehicle flows)
        if (chargerIsQualified && vehicleIsQualified) {
          void navigate(routes.PGE_PLUS_EV_CHARGERS_ENROLL_WITH_VEHICLE_OR_CHARGER);
          return;
        }

        // Charger not qualified but vehicle is; not described in AC56 business requirements. 
        // TODO: Update if needed. For now, navigate to fiddlesticks page..
        if (!chargerIsQualified && vehicleIsQualified) {
          navigateToIneligibleFiddlesticks();
          return;
        }

      } else if (response.errors) {
        const ineligibleErrors = response.errors?.filter(
          reason =>
            reason?.extensions?.code ===
              UpdateEVSectionErrorCode.ProductInactive ||
            reason?.extensions?.code ===
              UpdateEVSectionErrorCode.ProductOutOfStock ||
            reason?.extensions?.code ===
              UpdateEVSectionErrorCode.ProductNotAvailable,
        );
        if (ineligibleErrors.length > 0) {
          setErrorNotification(
            true,
            content.get('PRODUCT_NOT_AVAILABLE_ERROR'),
          );
          return;
        } else {
          throw new Error();
        }
      }
    } catch (e) {
      setErrorNotification(true);
    }
  });

  return (
    <Fragment>
      <form
        onSubmit={form.submit(values => onSubmit(values))}
        noValidate
      >
        <Box>
          <Typography css={{ ...subText1, ...boldText }}>
            {contentMCG.get('PGE_PLUS_EV_CHARGER_FLOW_READY_TO_START_YOUR_APPLICATION')}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body1">
            {contentMCG.get('PGE_PLUS_EV_CHARGER_FLOW_WE_NEED_SOME_INFORMATION')}
          </Typography>
        </Box>
        <Box display={'flex'} justifyContent={'center'}>
          <Grid container className={classes.informationBox} xs={12}>
            <Grid item xs={12}>
              <Typography
                variant={'body1'}
                css={{ fontWeight: 700, fontSize: 20, marginBottom: 15 }}
              >
                {contentMCG.get(
                  'PGE_PLUS_EV_CHARGER_FLOW_THE_INFORMATION_WE_NEED',
                )}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box className={classes.informationItem}>
                <DoneRounded className={classes.informationCheck} />
                <Typography variant="body1">
                  {contentMCG.get('PGE_PLUS_EV_CHARGER_FLOW_INFORMATION_ITEM_01')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box className={classes.informationItem}>
                <DoneRounded className={classes.informationCheck} />
                <Typography variant="body1">
                  {contentMCG.get('PGE_PLUS_EV_CHARGER_FLOW_INFORMATION_ITEM_02')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box className={classes.informationItem}>
                <DoneRounded className={classes.informationCheck} />
                <Typography variant="body1">
                  {contentMCG.get('PGE_PLUS_EV_CHARGER_FLOW_INFORMATION_ITEM_03')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box className={classes.informationItem}>
                <DoneRounded className={classes.informationCheck} />
                <Typography variant="body1">
                  {contentMCG.get('PGE_PLUS_EV_CHARGER_FLOW_INFORMATION_ITEM_04')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>      
        {/* Instruction: UPLOAD_PHOTO_CHARGER_MODEL_NUMBER */}
        <Box>
          <Typography css={{ ...subText1, ...boldText }}>
            {contentMCG.get('PGE_PLUS_EV_CHARGER_FLOW_UPLOAD_PHOTO_CHARGER_MODEL_NUMBER')}
          </Typography>
        </Box>
        <Box>
          <PopoverToolTip
            baseContent={getText('HOW_TO_FIND_YOUR_CHARGER_MODEL_NUMBER') || 'How to find your charger model number'}
            popoverContent={richText(
              getText('HOW_TO_FIND_MODEL_NUMBER_TOOLTIP') || '',
            )}
          />
        </Box>
        <Box mt={2}>
          <Fragment>
            {!form.values?.modelNumberImage && (
              <Box mt={3}>
                <Button
                  className={classes.imageTitle}
                  onClick={() => setOpenExamplePhotoModal(true)}
                  aria-label={getText('REUSABLE_VIEW_EXAMPLE_PHOTO') || 'View example photo'}
                >
                  {getText('REUSABLE_VIEW_EXAMPLE_PHOTO') || 'View example photo'}
                </Button>
                <Modal
                  open={openExamplePhotoModal}
                  onClose={() => setOpenExamplePhotoModal(false)}
                  title={content.get('MODEL_SAMPLE_PHOTO_TITLE')}
                  showCloseIcon={true}
                  modalStyles={{ width: 600 }}
                >
                  <Box mt={3}>
                    <img
                      css={{
                        width: '100%',
                        maxHeight: '350px',
                        objectFit: 'cover',
                      }}
                      src={getText('MODEL_SAMPLE_PHOTO_URL')}
                    />
                  </Box>
                </Modal>
              </Box>
            )}
            <FileUploaderWithOCR
              placeholder={fileUploaderText.get('UPLOAD_A_PHOTO')}
              reuploadLabel={fileUploaderText.get('REUPLOAD_PHOTO')!}
              ocrType={OcrFieldNames.ModelNumber}
              onComplete={async (key, extractedText, message, status) => {
                await form.setValue('modelNumberImage', { id: key });
                await form.setValue('modelNumber', extractedText);
                setOcrExtractionStatus(status);
              }}
              image={form.values?.modelNumberImage}
            />
            {form.errors?.modelNumberImage && (
              <FormHelperText className={classes.errorMessage} error>
                {form.errors?.modelNumberImage}
              </FormHelperText>
            )}

            {form.values?.modelNumberImage && (
              <Box
                css={{
                  marginBottom: theme.spacing(1),
                  '& .MuiFormControl-root': {
                    background: 'transparent',
                  },
                  '& .MuiInputBase-input': {
                    background: '#FFF',
                  },
                }}
              >
                {ocrExtractionStatus && ocrExtractionStatus === 'fail' && (
                  <FormHelperText
                    className={classes.errorMessage}
                    error
                    component="div"
                  >
                    {fileUploaderText.get('OCR_FAIL_MESSAGE')}
                  </FormHelperText>
                )}
                <Typography
                  css={{
                    fontSize: 20,
                    marginBottom: theme.spacing(2),
                    marginTop: theme.spacing(3),
                    fontWeight: 700,
                  }}
                >
                  {ocrExtractionStatus && ocrExtractionStatus === 'success'
                    ? content.get('REBATE_ONLY_EDIT_MODEL_HEADER')
                    : content.get('CHARGER_MODEL_NUMBER')}
                </Typography>
                <TextField
                  css={{
                    background: 'transparent',
                    '.MuiInputBase-formControl': {
                      background: colors.white,
                    },
                    width: '100%',
                  }}
                  name={'modelNumber'}
                  {...form.props('modelNumber')}
                />
                {ocrExtractionStatus && ocrExtractionStatus === 'success' && (
                  <Typography css={{ marginTop: 8 }}>
                    {content.get('MODEL_OCR_SUCCESS_MESSAGE')}
                  </Typography>
                )}
              </Box>
            )}
          </Fragment>
        </Box>

        {/* Instruction: SELECT_VEHICLE_MANUFACTURER */}
        <Box mt={4}>
        <Typography css={{ ...subText1, ...boldText }}>
            {contentMCG.get('PGE_PLUS_EV_CHARGER_FLOW_SELECT_VEHICLE_MANUFACTURER')}
          </Typography>
        </Box>

        <Box>
          <Grid item xs={12}>
            <DropdownMenu
              name={contentMCG.get('PGE_PLUS_EV_CHARGER_FLOW_DROPDOWN_NAME_VEHICLE_MANUFACTURER')}
              options={vehicleManufacturers}
              state={form.values.vehicleManufacturer || ''}
              setState={async e => {
                await form.setValue('vehicleManufacturer', e);
                await form.setValue('vehicleModel', '');
              }}
              errorMsg={(form.errors.vehicleManufacturer as string) || ''}
            />
          </Grid>
        </Box>

        {!!form.values.vehicleManufacturer &&
          vehicleModelOptions.length > 0 && (
            <Box mt={3}>
              <Grid item xs={12}>
                <DropdownMenu
                  name={contentMCG.get('PGE_PLUS_EV_CHARGER_FLOW_DROPDOWN_NAME_VEHICLE_MODEL')}
                  options={vehicleModelOptions}
                  state={form.values.vehicleModel || ''}
                  setState={async e => {
                    await form.setValue('vehicleModel', e);
                  }}
                  errorMsg={(form.errors.vehicleModel as string) || ''}
                />
              </Grid>
            </Box>
          )}

        <Divider css={{ color: colors.gray93, margin: '20px 0 0px 0' }} />

        <ProceedOrCancelButton 
          back={false} 
          isCancel={true}
          disable={
            !form.values?.modelNumber ||
            form.values?.modelNumberImage === undefined ||
            !form.values?.vehicleManufacturer ||
            (!!form.values.vehicleManufacturer &&
              vehicleModelOptions.length > 0 &&
              !form.values.vehicleModel)
          }
        />

        <Box mt={3}>
          <SaveAndComeBackLink
            content={contentMCG}
            onSaveAndExit={handleSaveAndExit}
          />
        </Box>
      </form>

      {selectCharger.suppliedByCustomer && <WelcomeToPGEPlusModal />}
    </Fragment>
  );
};
